import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ACCENT = '#B91C1C';
const MUTED = '#64748B';
const BORDER = '#D7DEE8';

export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const getSchoolReportProfile = () => {
  const schoolName = localStorage.getItem('schoolName') || 'School';
  const address =
    localStorage.getItem('schoolAddress') ||
    localStorage.getItem('address') ||
    'Address unavailable';
  const email = localStorage.getItem('schoolEmail') || localStorage.getItem('email') || '';
  const phone = localStorage.getItem('schoolPhone') || localStorage.getItem('phone') || '';
  const logoUrl = localStorage.getItem('schoolLogoUrl') || localStorage.getItem('logoUrl') || '';
  const contact = [email, phone].filter(Boolean).join(' | ') || 'Contact unavailable';

  return { schoolName, address, contact, logoUrl };
};

export const buildReportHtml = ({
  title,
  subtitle,
  school = getSchoolReportProfile(),
  body = '',
  orientation = 'portrait',
}) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 ${orientation}; margin: 14mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: #0f172a;
      background: #f8fafc;
      font-family: 'Inter', 'Segoe UI', sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet {
      background: #ffffff;
      border: 1px solid ${BORDER};
      border-radius: 22px;
      overflow: hidden;
    }
    .hero {
      padding: 22px 24px 18px;
      background:
        radial-gradient(circle at top right, rgba(185, 28, 28, 0.13), transparent 28%),
        linear-gradient(135deg, #fff7f7 0%, #ffffff 56%);
      border-bottom: 1px solid ${BORDER};
    }
    .brand {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 18px;
    }
    .brand-copy h1 {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: ${ACCENT};
      letter-spacing: -0.03em;
    }
    .brand-copy p {
      margin: 5px 0 0;
      color: ${MUTED};
      font-size: 12px;
    }
    .logo {
      max-height: 58px;
      max-width: 130px;
      object-fit: contain;
    }
    .title-block {
      margin-top: 18px;
      display: flex;
      justify-content: space-between;
      gap: 18px;
      align-items: flex-end;
      flex-wrap: wrap;
    }
    .title-block h2 {
      margin: 0;
      font-size: 20px;
      color: #111827;
    }
    .title-block .subtitle {
      margin-top: 6px;
      font-size: 12px;
      color: ${MUTED};
    }
    .stamp {
      padding: 9px 12px;
      border-radius: 999px;
      background: rgba(185, 28, 28, 0.08);
      color: ${ACCENT};
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .content {
      padding: 22px 24px 18px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }
    th {
      text-align: left;
      font-size: 10px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${MUTED};
      background: #f8fafc;
    }
    th, td {
      padding: 10px 12px;
      border-bottom: 1px solid ${BORDER};
      vertical-align: top;
    }
    tr:last-child td {
      border-bottom: none;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      margin-bottom: 18px;
    }
    .meta-card {
      border: 1px solid ${BORDER};
      border-radius: 16px;
      background: #fbfdff;
      padding: 12px 14px;
    }
    .meta-card .label {
      font-size: 10px;
      font-weight: 700;
      color: ${MUTED};
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .meta-card .value {
      margin-top: 6px;
      font-size: 13px;
      font-weight: 700;
      color: #111827;
    }
    .note {
      margin-top: 18px;
      padding: 12px 14px;
      border-radius: 16px;
      background: #fff7ed;
      color: #9a3412;
      font-size: 11px;
    }
    .footer {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: center;
      margin-top: 18px;
      padding-top: 14px;
      border-top: 1px solid ${BORDER};
      font-size: 11px;
      color: ${MUTED};
    }
    @media print {
      body { background: #ffffff; }
      .sheet { border: none; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="hero">
      <div class="brand">
        <div class="brand-copy">
          <h1>${escapeHtml(school.schoolName)}</h1>
          <p>${escapeHtml(school.address)}</p>
          <p>${escapeHtml(school.contact)}</p>
        </div>
        ${school.logoUrl ? `<img class="logo" src="${escapeHtml(school.logoUrl)}" alt="School Logo" />` : ''}
      </div>
      <div class="title-block">
        <div>
          <h2>${escapeHtml(title)}</h2>
          ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
        </div>
        <div class="stamp">Printed ${escapeHtml(format(new Date(), 'dd MMM yyyy, hh:mm a'))}</div>
      </div>
    </div>
    <div class="content">${body}</div>
  </div>
</body>
</html>`;

export const openPrintPreview = ({ title, html }) => {
  const printWindow = window.open('', '_blank', 'height=900,width=1200');
  if (!printWindow) {
    throw new Error('Pop-up blocked. Please allow pop-ups for printing.');
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    try {
      printWindow.print();
    } catch (error) {
      console.error('Print failed:', error);
    }
  }, 450);
  return printWindow;
};

export const downloadTablePdf = ({
  title,
  subtitle,
  filename,
  columns,
  rows,
  orientation = 'portrait',
  school = getSchoolReportProfile(),
}) => {
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(252, 242, 242);
  doc.roundedRect(32, 28, pageWidth - 64, 110, 18, 18, 'F');
  doc.setDrawColor(215, 222, 232);
  doc.roundedRect(32, 28, pageWidth - 64, 110, 18, 18, 'S');

  doc.setTextColor(185, 28, 28);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(21);
  doc.text(school.schoolName || 'School', 46, 58);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(String(school.address || 'Address unavailable'), 46, 77);
  doc.text(String(school.contact || 'Contact unavailable'), 46, 92);

  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(title, 46, 118);

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(subtitle, 46, 133);
  }

  autoTable(doc, {
    startY: 160,
    head: [columns],
    body: rows,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 7,
      lineColor: [215, 222, 232],
      lineWidth: 0.6,
      textColor: [15, 23, 42],
    },
    headStyles: {
      fillColor: [248, 250, 252],
      textColor: [100, 116, 139],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [252, 252, 253],
    },
    didDrawPage: () => {
      const footerY = doc.internal.pageSize.getHeight() - 20;
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated ${format(new Date(), 'dd MMM yyyy, hh:mm a')}`, 40, footerY);
    },
  });

  doc.save(filename);
};
